import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useState, useEffect } from "react";
import SurveyJsPanel from "./SurveyJsPanel";
import ThriveModal from "../../common/ThriveModal";
import ThriveTitle from "../../common/ThriveTitle";
// import BusinessNeedsAssessmentPanel from "./BusinessNeedsAssessmentPanel";
import { usePDF, Margin } from "react-to-pdf";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../../utils/networking";
import ThriveCaseSelection from "../../common/ThriveCaseSelection";

const Reports = () => {
  const { toPDF, targetRef } = usePDF({
    filename: "Report.pdf",
    page: { margin: Margin.SMALL, format: "letter" },
    overrides: {
      canvas: {
        removeContainer: false,
      },
    },
  });
  const [surveyOpen, setSurveyOpen] = useState(false);
  // const [bussNeedsSurveyOpen, setBussNeedsSurveyOpen] = useState(false);
  const [surveyResult, setSurveyResult] = useState<any[]>([]);
  const [surveyTitle, setSurveyTitle] = useState<string>("");
  const [surveyContent, setSurveyContent] = useState<any>({});
  const [metabaseQuestions, setMetabaseQuestions] = useState<
    Array<{ url: string; name: string }>
  >([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [allCaseCardOpen, setAllCaseCardOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<any>({
    _id: null,
    firmRecord: {},
  });
  const navigate = useNavigate();

  const refineSurveyContent = (surveyContent: any) => {
    if (surveyContent.pages) {
      for (const pageContent of surveyContent.pages) {
        const pageTitle = pageContent.title;
        for (const elementContent of pageContent.elements) {
          elementContent.title = pageTitle + " - " + elementContent.title;
        }
      }
    }
    return surveyContent;
  };

  const getSurvey = async (survey_id: any) => {
    const response = await axiosInstance.get(`/survey?id=${survey_id}`);
    const refinedSurveyContent = refineSurveyContent(
      response.data.survey.survey_content
    );
    setSurveyContent(refinedSurveyContent);
    return response.data.survey;
  };

  const getSurveyResult = async (survey_id: any) => {
    const response = await axiosInstance.get(
      `/survey/results?survey_id=${survey_id}`
    );
    setSurveyResult(
      response.data.results.map((result: any) => result.survey_result)
    );
    return response.data.results;
  };

  const getBFC = async (case_id: any) => {
    const response = await axiosInstance.get(`/user?case_id=${case_id}`);
    return response.data.user.firstName + " " + response.data.user.lastName;
  };

  const getCompany = async (case_id: any) => {
    const response = await axiosInstance.get(`/firms/id?case_id=${case_id}`);
    return response.data.firm;
  };

  const fetchMetabaseQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const response = await axiosInstance.get("/reports/metabase/questions");
      if (response.status === 200) {
        setMetabaseQuestions(response.data.questions || []);
      }
    } catch (error) {
      console.error("Error fetching metabase questions:", error);
    } finally {
      setLoadingQuestions(false);
    }
  };

  useEffect(() => {
    fetchMetabaseQuestions();
  }, []);

  const handleReferralSurvey = async () => {
    axiosInstance
      .get(`/survey/results?survey_id=64f75c4287cfd9719d4904e1`)
      .then(async (res) => {
        if (res.status === 200) {
          // console.log(res.data);
          let csvContent: any[] = [];
          let csvHeading = [
            "SBC",
            "Company",
            "Contact",
            "Email",
            "Survey Date",
          ];
          let csvNames: string[] = [];
          res.data.survey.survey_content.elements.map((survey_q: any) => {
            if (survey_q.title) {
              if (survey_q.description) {
                csvHeading.push(
                  survey_q.title + "(" + survey_q.description + ")"
                );
              } else {
                csvHeading.push(survey_q.title);
              }

              csvNames.push(survey_q.name);
            }
            return null;
          });
          csvContent.push(csvHeading);

          for (const result of res.data.results) {
            let csvAnswer = [];
            const BFC = await getBFC(result["case_id"]);
            csvAnswer.push(BFC);
            const Company = await getCompany(result["case_id"]);
            csvAnswer.push(Company["Company Name"]);
            csvAnswer.push(
              Company["Contact"]
                ? Company["Contact"]
                : Company["additionalContacts"][0]["Contact"]
            );
            csvAnswer.push(Company["Email"]);
            csvAnswer.push(dayjs(result.updated_at).format("YYYY-MM-DD"));

            for (const q of csvNames) {
              if (result["survey_result"][q]) {
                csvAnswer.push(result["survey_result"][q]);
              } else {
                csvAnswer.push("");
              }
            }
            csvContent.push(csvAnswer);
          }

          let csvString = ``;
          for (const row of csvContent) {
            let rowString = `\n`;
            for (const item of row) {
              rowString = rowString + `"${item}",`;
            }
            csvString = csvString + rowString;
          }

          const blob = new Blob([csvString], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);

          // Create a hidden anchor element
          const a = document.createElement("a");
          a.href = url;
          a.download = "referral_survey.csv";
          document.body.appendChild(a);

          // Trigger the download
          a.click();

          // Clean up
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          console.log("API Error");
        }
      });
  };

  const handleEventSurvey = async () => {
    axiosInstance
      .get(`/survey/results?survey_id=65412693f9ecc07bf529219f`)
      .then((res) => {
        if (res.status === 200) {
          let csvContent: any[] = [];
          let csvHeading = [
            "First Name",
            "Last Name",
            "Email",
            "What event did you attend?",
            "How likely are you to recommend this event to a friend or colleague?",
            "Overall how satisfied are you with the event?",
            "How likely are you to join Oakland Thrive for another event?",
            "Quality of venue?",
            "What other training topics would you like to see offered by Oakland Thrive?",
            "Any other comments or suggestions?",
            "How did you hear about this event/Oakland Thrive?",
            "Other",
            "Can we use your comments, name and company name on Oakland Thrive promotional materials in the future?",
          ];
          let csvNames: string[] = [];
          csvContent.push(csvHeading);

          for (const surveyItem of res.data.results) {
            let csvAnswer = [];
            csvAnswer.push(
              surveyItem.survey_result["first-name"]
                ? surveyItem.survey_result["first-name"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["last-name"]
                ? surveyItem.survey_result["last-name"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.email
                ? surveyItem.survey_result.email
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q1 ? surveyItem.survey_result.q1 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q2 ? surveyItem.survey_result.q2 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q3 ? surveyItem.survey_result.q3 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q4 ? surveyItem.survey_result.q4 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q5 ? surveyItem.survey_result.q5 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q6 ? surveyItem.survey_result.q6 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q7 ? surveyItem.survey_result.q7 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q8 ? surveyItem.survey_result.q8 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["q8-Comment"]
                ? surveyItem.survey_result["q8-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q9 ? surveyItem.survey_result.q9 : ""
            );
            csvContent.push(csvAnswer);
          }

          let csvString = ``;
          for (const row of csvContent) {
            let rowString = `\n`;
            for (const item of row) {
              rowString = rowString + `"${item}",`;
            }
            csvString = csvString + rowString;
          }

          const blob = new Blob([csvString], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);

          // Create a hidden anchor element
          const a = document.createElement("a");
          a.href = url;
          a.download = `event survey.csv`;
          document.body.appendChild(a);

          // Trigger the download
          a.click();

          // Clean up
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      });
  };

  const handleConferenceSurvey = async () => {
    axiosInstance
      .get(`/survey/results?survey_id=654b20727276d3350fde26ba`)
      .then((res) => {
        if (res.status === 200) {
          let csvContent: any[] = [];
          let csvHeading = [
            "First Name",
            "Last Name",
            "Email",
            "What event did you attend?",
            "How likely are you to recommend this conference to a friend or colleague?",
            "Overall how satisfied are you with the conference?",
            "How likely are you to join us at the conference next year?",
            "Quality of venue?",
            " What other topics would you like to see covered at the next conference?",
            "Any other comments or suggestions?",
            "How did you hear about the conference?",
            "Other",
            "Can we use your comments, name and company name on conference promotional materials in the future?",
          ];
          let csvNames: string[] = [];
          csvContent.push(csvHeading);

          for (const surveyItem of res.data.results) {
            let csvAnswer = [];
            csvAnswer.push(
              surveyItem.survey_result["first-name"]
                ? surveyItem.survey_result["first-name"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["last-name"]
                ? surveyItem.survey_result["last-name"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.email
                ? surveyItem.survey_result.email
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q1 ? surveyItem.survey_result.q1 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q2 ? surveyItem.survey_result.q2 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q3 ? surveyItem.survey_result.q3 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q4
                ? surveyItem.survey_result.q4
                : "Defense & Advanced Manufacturing Conference"
            );
            csvAnswer.push(
              surveyItem.survey_result.q5 ? surveyItem.survey_result.q5 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q6 ? surveyItem.survey_result.q6 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q7 ? surveyItem.survey_result.q7 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q8 ? surveyItem.survey_result.q8 : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["q8-Comment"]
                ? surveyItem.survey_result["q8-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result.q9 ? surveyItem.survey_result.q9 : ""
            );
            csvContent.push(csvAnswer);
          }

          let csvString = ``;
          for (const row of csvContent) {
            let rowString = `\n`;
            for (const item of row) {
              rowString = rowString + `"${item}",`;
            }
            csvString = csvString + rowString;
          }

          const blob = new Blob([csvString], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);

          // Create a hidden anchor element
          const a = document.createElement("a");
          a.href = url;
          a.download = `event survey.csv`;
          document.body.appendChild(a);

          // Trigger the download
          a.click();

          // Clean up
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      });
  };

  const handleB2BClientSurvey = async () => {
    axiosInstance
      .get(`/survey/results?survey_id=65585ca5da7bea193668034e`)
      .then((res) => {
        if (res.status === 200) {
          let csvContent: any[] = [];
          let csvHeading = [
            "Contact Name",
            "Small Business Name",
            "Small Business Contact Person",
            "Professional Business Provider Name",
            "Place of Meeting",
            "Time Spent",
            "Contact Date",
            "Today's Date",
            "The Meeting was well planned (Communications and Scheduling)",
            "Notes",
            "The Content of the meeting was informative and useful for me",
            "Notes",
            "My business needs were met",
            "Notes",
            "The provider I met with was prepared, professional and knowledgeable",
            "Notes",
            "Were you satisfied with the overall meeting experience?",
            "Notes",
            "How likely are you to recommend the Professional Business Provider",
            "Notes",
            "What questions were you left with after this meeting?",
            "Next Steps",
            "Do you have any suggestions or additional comments regarding your meeting?",
          ];
          let csvNames: string[] = [];
          csvContent.push(csvHeading);

          for (const surveyItem of res.data.results) {
            let csvAnswer = [];
            csvAnswer.push(
              surveyItem.survey_result["question1"]
                ? surveyItem.survey_result["question1"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question2"]
                ? surveyItem.survey_result["question2"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question3"]
                ? surveyItem.survey_result["question3"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question4"]
                ? surveyItem.survey_result["question4"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question5"]
                ? surveyItem.survey_result["question5"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question6"]
                ? surveyItem.survey_result["question6"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question7"]
                ? surveyItem.survey_result["question7"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question8"]
                ? surveyItem.survey_result["question8"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question9"]
                ? surveyItem.survey_result["question9"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question9-Comment"]
                ? surveyItem.survey_result["question9-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question10"]
                ? surveyItem.survey_result["question10"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question10-Comment"]
                ? surveyItem.survey_result["question10-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question11"]
                ? surveyItem.survey_result["question11"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question11-Comment"]
                ? surveyItem.survey_result["question11-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question12"]
                ? surveyItem.survey_result["question12"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question12-Comment"]
                ? surveyItem.survey_result["question12-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question13"]
                ? surveyItem.survey_result["question13"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question13-Comment"]
                ? surveyItem.survey_result["question13-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question14"]
                ? surveyItem.survey_result["question14"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question14-Comment"]
                ? surveyItem.survey_result["question14-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question15"]
                ? surveyItem.survey_result["question15"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question16"]
                ? surveyItem.survey_result["question16"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question17"]
                ? surveyItem.survey_result["question17"]
                : ""
            );

            csvContent.push(csvAnswer);
          }

          let csvString = ``;
          for (const row of csvContent) {
            let rowString = `\n`;
            for (const item of row) {
              rowString = rowString + `"${item}",`;
            }
            csvString = csvString + rowString;
          }

          const blob = new Blob([csvString], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);

          // Create a hidden anchor element
          const a = document.createElement("a");
          a.href = url;
          a.download = `b2b client survey.csv`;
          document.body.appendChild(a);

          // Trigger the download
          a.click();

          // Clean up
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      });
  };

  const handleB2BProviderSurvey = async () => {
    axiosInstance
      .get(`/survey/results?survey_id=655869c8da7bea1936680388`)
      .then((res) => {
        if (res.status === 200) {
          let csvContent: any[] = [];
          let csvHeading = [
            "PBP Business Name",
            "Small Business Name",
            "Small Business Contact Person",
            "Provider Name",
            "Place of Meeting",
            "Time Spent",
            "Contact Date",
            "Today's Date",
            "Was the meeting well planned and executed?",
            "Notes",
            "Did the Business come prepared with the necessary documents",
            "Notes",
            "Was sufficient time allocated for the meeting?",
            "Notes",
            "Was the meeting successful and 'as needed'?",
            "Notes",
            "Was the meeting 'tone and participation level' sufficiently positive and productive?",
            "Notes",
            "Were all follow-up actions and 'next steps' properly identified, recorded and tracked?",
            "Notes",
            "Services Provided",
            "Next Steps",
            "COMMENTS",
          ];
          let csvNames: string[] = [];
          csvContent.push(csvHeading);

          for (const surveyItem of res.data.results) {
            let csvAnswer = [];
            csvAnswer.push(
              surveyItem.survey_result["question1"]
                ? surveyItem.survey_result["question1"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question2"]
                ? surveyItem.survey_result["question2"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question3"]
                ? surveyItem.survey_result["question3"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question4"]
                ? surveyItem.survey_result["question4"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question5"]
                ? surveyItem.survey_result["question5"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question6"]
                ? surveyItem.survey_result["question6"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question7"]
                ? surveyItem.survey_result["question7"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question8"]
                ? surveyItem.survey_result["question8"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question9"]
                ? surveyItem.survey_result["question9"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question9-Comment"]
                ? surveyItem.survey_result["question9-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question10"]
                ? surveyItem.survey_result["question10"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question10-Comment"]
                ? surveyItem.survey_result["question10-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question11"]
                ? surveyItem.survey_result["question11"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question11-Comment"]
                ? surveyItem.survey_result["question11-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question12"]
                ? surveyItem.survey_result["question12"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question12-Comment"]
                ? surveyItem.survey_result["question12-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question13"]
                ? surveyItem.survey_result["question13"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question13-Comment"]
                ? surveyItem.survey_result["question13-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question14"]
                ? surveyItem.survey_result["question14"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question14-Comment"]
                ? surveyItem.survey_result["question14-Comment"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question15"]
                ? surveyItem.survey_result["question15"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question16"]
                ? surveyItem.survey_result["question16"]
                : ""
            );
            csvAnswer.push(
              surveyItem.survey_result["question17"]
                ? surveyItem.survey_result["question17"]
                : ""
            );

            csvContent.push(csvAnswer);
          }

          let csvString = ``;
          for (const row of csvContent) {
            let rowString = `\n`;
            for (const item of row) {
              rowString = rowString + `"${item}",`;
            }
            csvString = csvString + rowString;
          }

          const blob = new Blob([csvString], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);

          // Create a hidden anchor element
          const a = document.createElement("a");
          a.href = url;
          a.download = `b2b provider survey.csv`;
          document.body.appendChild(a);

          // Trigger the download
          a.click();

          // Clean up
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      });
  };

  const handleActiveClients = async () => {
    axiosInstance.get(`/reports/active_clients`).then((response: any) => {
      if (response.status === 200) {
        let csvContent: any[] = [];
        let csvHeading = ["Active Clients", "City"];
        let csvNames: string[] = [];
        csvContent.push(csvHeading);

        for (const clients of response.data.activeClients) {
          let csvAnswer = [];
          csvAnswer.push(clients["Company Name"]);
          csvAnswer.push(clients["City"]);
          csvContent.push(csvAnswer);
        }

        let csvString = ``;
        for (const row of csvContent) {
          let rowString = `\n`;
          for (const item of row) {
            rowString = rowString + `"${item}",`;
          }
          csvString = csvString + rowString;
        }

        const blob = new Blob([csvString], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);

        // Create a hidden anchor element
        const a = document.createElement("a");
        a.href = url;
        a.download = `b2b provider survey.csv`;
        document.body.appendChild(a);

        // Trigger the download
        a.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    });
  };

  return (
    <>
      <Box sx={{ width: "80%", margin: "auto", mb: 3, textAlign: "center" }}>
        <Button
          variant="contained"
          sx={{
            fontSize: "1.1rem",
            padding: "12px 24px",
            borderRadius: "8px",
            backgroundColor: "#9D2362",
            color: "white",
            "&:hover": {
              backgroundColor: "#7A1B4D",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            },
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
          onClick={() => {
            // Check if we're on staging environment
            const reportUrl =
              process.env.REACT_APP_API_DOMAIN !==
              "https://www.thrive-drive.org:8443"
                ? "http://23.26.250.185/metabase"
                : "https://report.thrive-drive.org";

            window.open(reportUrl, "_blank");
          }}
        >
          Report Dashboard
        </Button>
        <Button
          variant="contained"
          sx={{
            fontSize: "1.1rem",
            padding: "12px 24px",
            borderRadius: "8px",
            backgroundColor: "#3F51B5",
            color: "white",
            "&:hover": {
              backgroundColor: "#303F9F",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            },
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            ml: 2,
          }}
          onClick={() => {
            const learningURL = "https://www.metabase.com/learn/";

            window.open(learningURL, "_blank");
          }}
        >
          Learn Reporting
        </Button>
      </Box>

      <Table sx={{ width: "80%", margin: "auto" }}>
        <TableBody>
          <TableRow>
            <TableCell>
              <Button
                onClick={() => {
                  setAllCaseCardOpen(true);
                }}
              >
                Active Clients Report
              </Button>
            </TableCell>
            <TableCell>
              <Button onClick={handleActiveClients}>Download Raw</Button>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Button
                onClick={() => {
                  navigate("/dashboard/reports/initial_contact");
                }}
              >
                Initial Contacts Report
              </Button>
            </TableCell>
            <TableCell>{/* <Button>Download Raw</Button> */}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Button
                onClick={() => {
                  navigate("/dashboard/reports/rga");
                }}
              >
                Recovery Grant Applicants Report
              </Button>
            </TableCell>
            <TableCell>{/* <Button>Download Raw</Button> */}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Button
                onClick={() => {
                  getSurvey("6488788edd1f6f5e5c5e5d7a");
                  getSurveyResult("6488788edd1f6f5e5c5e5d7a");
                  setSurveyTitle("Discovery Question Report");
                  setSurveyOpen(!surveyOpen);
                }}
              >
                Discovery Question Report
              </Button>
            </TableCell>
            <TableCell>{/* <Button>Download Raw</Button> */}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Button
                onClick={() => {
                  getSurvey("6504ae6817baa329c061dee7");
                  getSurveyResult("6504ae6817baa329c061dee7");
                  setSurveyTitle("Business Needs Assessment Report");
                  // setBussNeedsSurveyOpen(!bussNeedsSurveyOpen);
                  setSurveyOpen(!surveyOpen);
                }}
              >
                Business Needs Assessment Report
              </Button>
            </TableCell>
            <TableCell>{/* <Button>Download Raw</Button> */}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Button
                onClick={() => {
                  getSurvey("64f75c4287cfd9719d4904e1");
                  getSurveyResult("64f75c4287cfd9719d4904e1");
                  setSurveyTitle("Resource Referral Report");
                  setSurveyOpen(!surveyOpen);
                }}
              >
                Resource Referral Surveys
              </Button>
            </TableCell>
            <TableCell>
              <Button onClick={handleReferralSurvey}>Download Raw</Button>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Button
                onClick={() => {
                  getSurvey("65412693f9ecc07bf529219f");
                  getSurveyResult("65412693f9ecc07bf529219f");
                  setSurveyTitle("Event Survey Report");
                  setSurveyOpen(!surveyOpen);
                }}
              >
                Event Surveys
              </Button>
            </TableCell>
            <TableCell>
              <Button onClick={handleEventSurvey}>Download Raw</Button>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Button
                onClick={() => {
                  getSurvey("654b20727276d3350fde26ba");
                  getSurveyResult("654b20727276d3350fde26ba");
                  setSurveyTitle("Conference Survey Report");
                  setSurveyOpen(!surveyOpen);
                }}
              >
                Conference Surveys
              </Button>
            </TableCell>
            <TableCell>
              <Button onClick={handleConferenceSurvey}>Download Raw</Button>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Button
                onClick={() => {
                  getSurvey("65585ca5da7bea193668034e");
                  getSurveyResult("65585ca5da7bea193668034e");
                  setSurveyTitle("B2B Client Survey Report");
                  setSurveyOpen(!surveyOpen);
                }}
              >
                B2B Client Surveys
              </Button>
            </TableCell>
            <TableCell>
              <Button onClick={handleB2BClientSurvey}>Download Raw</Button>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Button
                onClick={() => {
                  getSurvey("655869c8da7bea1936680388");
                  getSurveyResult("655869c8da7bea1936680388");
                  setSurveyTitle("B2B Vendor Survey Report");
                  setSurveyOpen(!surveyOpen);
                }}
              >
                B2B Vendor Surveys
              </Button>
            </TableCell>
            <TableCell>
              <Button onClick={handleB2BProviderSurvey}>Download Raw</Button>
            </TableCell>
          </TableRow>

          {/* Metabase Questions */}
          {loadingQuestions ? (
            <TableRow>
              <TableCell colSpan={2}>
                <Typography sx={{ textAlign: "center" }}>
                  Loading metabase reports...
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            metabaseQuestions.map((question, index) => (
              <TableRow key={`metabase-${index}`}>
                <TableCell>
                  <Button onClick={() => window.open(question.url, "_blank")}>
                    {question.name}
                  </Button>
                </TableCell>
                <TableCell>
                  {/* No download option for metabase reports */}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <ThriveModal
        open={surveyOpen}
        width={"1000px"}
        height={"600px"}
        contentComponent={
          <>
            <ThriveTitle
              title={surveyTitle}
              setOpen={setSurveyOpen}
            ></ThriveTitle>
            <Box sx={{ position: "absolute", marginTop: "-42px" }}>
              <Button
                sx={{ marginLeft: "10px" }}
                color={"warning"}
                variant="contained"
                onClick={() => toPDF()}
              >
                Print
              </Button>
            </Box>

            <Box
              sx={{
                margin: 1,
                height: "530px",
                overflow: "auto",
                backgroundColor: "white",
              }}
            >
              {surveyOpen && surveyContent && surveyResult ? (
                <SurveyJsPanel
                  surveyTitle={surveyTitle}
                  pdfref={targetRef}
                  surveyContent={surveyContent}
                  surveyResult={surveyResult}
                ></SurveyJsPanel>
              ) : (
                <></>
              )}
            </Box>
          </>
        }
      ></ThriveModal>
      {/* <ThriveModal
        open={bussNeedsSurveyOpen}
        width={"1000px"}
        height={"600px"}
        contentComponent={
          <>
            <ThriveTitle
              title={surveyTitle}
              setOpen={setBussNeedsSurveyOpen}
            ></ThriveTitle>
            <Box
              sx={{
                margin: 1,
                height: "530px",
                overflow: "auto",
                backgroundColor: "white",
              }}
            >
              {bussNeedsSurveyOpen && surveyContent && surveyResult ? (
                <BusinessNeedsAssessmentPanel
                  surveyContent={surveyContent}
                  surveyResult={surveyResult}
                ></BusinessNeedsAssessmentPanel>
              ) : (
                <></>
              )}
            </Box>
          </>
        }
      ></ThriveModal> */}
      <ThriveModal
        height={700}
        open={allCaseCardOpen}
        contentComponent={
          <>
            <ThriveCaseSelection
              height={500}
              cases={[]}
              reloadCases={true}
              checkedCases={[]}
              setCheckedCases={() => {}}
              selectedCase={selectedCase}
              setSelectedCase={setSelectedCase}
              enableControl={true}
              enableSelection={true}
              enableMultiSelection={false}
              setOpen={setAllCaseCardOpen}
              title={"All Cases"}
              status_default="either"
              sortby_default="recent"
              period_default="all"
              handleNext={() => {
                console.log("Next button clicked");
                if (selectedCase._id) {
                  window.open(
                    `/firmReportPdf?id=${selectedCase._id}`,
                    "_blank"
                  );
                } else {
                  console.error("No case selected");
                }
              }}
            ></ThriveCaseSelection>
          </>
        }
      />
    </>
  );
};

export default Reports;
